import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchAudiences, fetchContent, fetchLocales } from "./api/client";
import type { Audience, ContentBundle, Locale } from "./api/types";
import { ModeToggle } from "./components/ModeToggle";
import { QuizScreen } from "./screens/QuizScreen";
import { LetterScreen } from "./screens/LetterScreen";
import { ChatScreen } from "./screens/ChatScreen";
import { localUi } from "./i18n/uiFallback";

type Mode = "letter" | "chat";

const LS = {
  locale: "bv_locale",
  audience: "bv_audience",
  tone: "bv_tone",
  dark: "bv_dark",
  saved: "bv_saved",
  mode: "bv_mode_override",
} as const;

function readLS<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : (JSON.parse(v) as T);
  } catch {
    return fallback;
  }
}

function writeLS(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode etc — ignore */
  }
}

function negotiateInitialLocale(supported: string[]): string {
  const stored = readLS<string | null>(LS.locale, null);
  if (stored && supported.includes(stored)) return stored;
  const nav = (navigator.languages ?? [navigator.language ?? "de"])
    .map((l) => l.split("-")[0])
    .find((l) => supported.includes(l));
  return nav ?? "de";
}

export function StudioApp() {
  const [locales, setLocales] = useState<Locale[]>([]);
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [locale, setLocaleState] = useState<string>("de");
  // setTone is wired into the Tweaks-Panel surface in V1.1 — for now the value
  // is only read from localStorage and passed through to the bundle request.
  const [tone] = useState<"soft" | "direct">(() =>
    readLS<"soft" | "direct">(LS.tone, "soft"),
  );
  const [dark, setDark] = useState<boolean>(() => readLS<boolean>(LS.dark, false));
  const [audienceSlug, setAudienceSlug] = useState<string>(() =>
    readLS<string>(LS.audience, ""),
  );
  const [modeOverride, setModeOverride] = useState<Mode | null>(() =>
    readLS<Mode | null>(LS.mode, null),
  );
  const [bundle, setBundle] = useState<ContentBundle | null>(null);
  const [bundleError, setBundleError] = useState<string | null>(null);
  const [bundleLoading, setBundleLoading] = useState(false);
  const [filterCat, setFilterCat] = useState<string>("all");
  const [savedIds, setSavedIds] = useState<string[]>(() => readLS<string[]>(LS.saved, []));

  const setLocale = useCallback((next: string) => {
    setLocaleState(next);
    writeLS(LS.locale, next);
  }, []);

  // Boot: load locales + initial audience list
  useEffect(() => {
    (async () => {
      try {
        const ls = await fetchLocales();
        setLocales(ls);
        const initial = negotiateInitialLocale(ls.map((l) => l.code));
        setLocaleState(initial);
      } catch (e) {
        setBundleError(`${e}`);
      }
    })();
  }, []);

  // Audience list refreshes when locale changes
  useEffect(() => {
    if (!locale) return;
    (async () => {
      try {
        setAudiences(await fetchAudiences(locale));
      } catch (e) {
        setBundleError(`${e}`);
      }
    })();
  }, [locale]);

  // Bundle loads when audience changes
  useEffect(() => {
    if (!audienceSlug) {
      setBundle(null);
      return;
    }
    setBundleLoading(true);
    setBundleError(null);
    fetchContent(audienceSlug, locale, tone)
      .then((b) => {
        setBundle(b);
        setFilterCat("all");
      })
      .catch((e) => setBundleError(`${e}`))
      .finally(() => setBundleLoading(false));
  }, [audienceSlug, locale, tone]);

  // Persist
  useEffect(() => writeLS(LS.audience, audienceSlug), [audienceSlug]);
  useEffect(() => writeLS(LS.tone, tone), [tone]);
  useEffect(() => writeLS(LS.dark, dark), [dark]);
  useEffect(() => writeLS(LS.saved, savedIds), [savedIds]);
  useEffect(() => writeLS(LS.mode, modeOverride), [modeOverride]);

  // Effective mode
  const effectiveMode: Mode = useMemo(() => {
    if (modeOverride) return modeOverride;
    return bundle?.default_mode ?? "letter";
  }, [modeOverride, bundle]);

  // Apply HTML data attributes (drives CSS variables)
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.mode = effectiveMode;
    root.dataset.theme = dark ? "dark" : "light";
    root.lang = locale;
  }, [effectiveMode, dark, locale]);

  // Audience pattern for cover
  const audiencePattern = useMemo(() => {
    return audiences.find((a) => a.slug === audienceSlug)?.pattern ?? "activist";
  }, [audiences, audienceSlug]);

  const pickAudience = (slug: string) => {
    setAudienceSlug(slug);
    setModeOverride(null);
  };

  const backToQuiz = () => {
    setAudienceSlug("");
    setModeOverride(null);
    setBundle(null);
  };

  const toggleSaved = (id: string) =>
    setSavedIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  // Quiz UI strings: from audiences API we don't get the UI catalog —
  // we ship the minimum locally; full strings load with the bundle.
  const quizUi = bundle?.ui ?? minimalQuizUi(locale);

  if (bundleError && !bundle) {
    return (
      <div className="status-overlay">
        <div className="status-overlay__card">
          <h2 className="status-overlay__title">Connection lost</h2>
          <p>{bundleError}</p>
          <p className="mono" style={{ marginTop: 16 }}>
            {localUi(locale, "fallback_hint")}
          </p>
        </div>
      </div>
    );
  }

  if (!locales.length || !audiences.length) {
    return (
      <div className="status-overlay">
        <div className="status-overlay__card">
          <div className="mono">— LOADING —</div>
        </div>
      </div>
    );
  }

  if (!audienceSlug) {
    return (
      <QuizScreen
        audiences={audiences}
        locales={locales}
        locale={locale}
        ui={quizUi}
        dark={dark}
        onPick={pickAudience}
        onLocale={setLocale}
        onDarkToggle={() => setDark((d) => !d)}
      />
    );
  }

  if (bundleLoading || !bundle) {
    return (
      <div className="status-overlay">
        <div className="status-overlay__card">
          <div className="mono">— LOADING {audienceSlug.toUpperCase()} —</div>
        </div>
      </div>
    );
  }

  return (
    <>
      {effectiveMode === "chat" ? (
        <ChatScreen
          key={`chat-${bundle.audience_slug}-${bundle.locale}-${bundle.tone}`}
          bundle={bundle}
          tone={tone}
          locales={locales}
          requestedLocale={locale}
          onLocale={setLocale}
          savedIds={savedIds}
          toggleSaved={toggleSaved}
          onBack={backToQuiz}
        />
      ) : (
        <LetterScreen
          key={`letter-${bundle.audience_slug}-${bundle.locale}-${bundle.tone}`}
          bundle={bundle}
          audiencePattern={audiencePattern}
          tone={tone}
          locales={locales}
          requestedLocale={locale}
          onLocale={setLocale}
          filterCat={filterCat}
          setFilterCat={setFilterCat}
          savedIds={savedIds}
          toggleSaved={toggleSaved}
          onBack={backToQuiz}
          onSwitchAudience={backToQuiz}
        />
      )}
      <ModeToggle mode={effectiveMode} onChange={setModeOverride} locale={locale} />
    </>
  );
}

// Minimal UI fallback for the Quiz before any bundle has loaded.
// Once an audience is picked, the bundle carries the full catalog.
function minimalQuizUi(locale: string): Record<string, string> {
  const de: Record<string, string> = {
    brand: "Werde Vegan",
    tagline: "Ein Dialog für jede Lebensrealität.",
    quiz_title: "Wer bist du?",
    quiz_sub: "Wähle die Perspektive, die dich am meisten beschreibt. Wir passen den Dialog an.",
    keep_going: "Weiter lesen",
    page_of: "von",
    lang: "Sprache",
    dark: "Dunkel",
    light: "Hell",
  };
  const en: Record<string, string> = {
    brand: "Become Vegan",
    tagline: "A dialogue for every life.",
    quiz_title: "Who are you?",
    quiz_sub: "Choose the perspective that best describes you. We'll tune the conversation.",
    keep_going: "Keep reading",
    page_of: "of",
    lang: "Language",
    dark: "Dark",
    light: "Light",
  };
  return locale === "de" ? de : en;
}
